<template>
	<div
		class="h-full">
		<div class="w-full mb-5 flex justify-between">
			<h1 class="text-3xl font-black text-dbBlue">
				Dashboard
			</h1>
		</div>
		<div class="grid grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
			<!-- shares the row with Licensing on wide screens, and takes the whole row when Licensing is hidden -->
			<div
				class="card col-span-2"
				:class="{'xl:col-span-1': showLicensing}">
				<div class="flex flex-wrap items-center justify-between sm:flex-nowrap px-4 py-[14px] sm:px-6 bg-white relative overflow-hidden">
					<div>
						<h2 class="card-heading">
							Payments
						</h2>
					</div>
					<card-header-backdrop class="opacity-30" />
				</div>
				<div class="px-4 py-5 sm:p-6 bg-white z-20">
					<dl class="grid grid-cols-1 gap-5 sm:grid-cols-2">
						<div class="overflow-hidden bg-white">
							<tw-simple-stat
								:loading="loading"
								label="Past Due"
								value-class="text-red-600">
								{{ currencyFormatter.format(totalPastDue) }}
							</tw-simple-stat>
						</div>
						<div class="overflow-hidden bg-white">
							<tw-simple-stat
								:loading="loading"
								label="Total Outstanding">
								{{ currencyFormatter.format(totalOutstanding) }}
							</tw-simple-stat>
						</div>
					</dl>
				</div>
			</div>

			<div
				v-if="showLicensing"
				class="card col-span-2 xl:col-span-1">
				<div class="flex flex-wrap items-center justify-between sm:flex-nowrap px-4 py-[14px] sm:px-6 bg-white overflow-hidden relative">
					<div>
						<h2 class="card-heading">
							Licensing
						</h2>
					</div>
					<div class="flex-shrink-0">
						<a 
							:href="'https://accounts.claris.com/software/license/' + licenses[0]?.key"
							target="_blank">
							<tw-button 
								class="btn-base btn-indigo w-44">
								<template #icon_right>
									<ArrowDownTrayIconMini class="w-5 h-5" />
								</template>
								Download License
							</tw-button>
						</a>
					</div>
					<card-header-backdrop class="opacity-30" />
				</div>
				<div class="px-4 py-5 sm:p-6">
					<dl class="grid grid-cols-1 gap-5 xl:grid-cols-2">
						<div class="overflow-hidden bg-white">
							<tw-simple-stat
								:loading="loading"
								label="License Expires">
								{{ licenses[0]?.expirationDate }}
							</tw-simple-stat>
						</div>
						<div class="overflow-hidden bg-white">
							<tw-simple-stat
								:loading="loading"
								label="Days Until Expiration"
								unit="Days Remaining">
								{{ licenses[0]?.daysToExpire }}
							</tw-simple-stat>
						</div>
					</dl>
				</div>
			</div>

			<div class="card col-span-2 min-w-0">
				<div class="flex flex-wrap items-center justify-between sm:flex-nowrap px-4 py-[14px] sm:px-6 bg-white relative overflow-hidden">
					<div>
						<h2 class="card-heading">
							Current Invoices
						</h2>
					</div>
					<div class="flex-shrink-0">
						<router-link :to="{name: 'Invoices'}">
							<tw-button 
								class="btn-base btn-indigo w-44">
								<template #icon_right>
									<ListBulletIconMini class="w-5 h-5" />
								</template>
								View All Invoices
							</tw-button>
						</router-link>
					</div>
					<card-header-backdrop class="opacity-20" />
				</div>
				<div class="px-4 py-5 sm:p-6">
					<tw-table
						:data="invoices"
						:loading="loading"
						placeholder="No Open Invoices">
						<tw-table-column
							header="Invoice #" 
							prop="invoiceNumber"
							class-name="font-bold text-gray-900">
							<template #default="{row}">
								{{ row.invoiceNumber }}
								<div class="md:hidden !font-normal mt-4">
									<tw-badge-flat
										v-if="showStatusBadge(row)"
										:color="invoiceStatusColor(row)">
										{{ invoiceStatusLabel(row) }}
									</tw-badge-flat>
									<span v-else>
										{{ row.paymentTerms }}
									</span>
								</div>
							</template>
						</tw-table-column>
						<tw-table-column
							header="Amount Due">
							<template #default="{row}">
								<span :class="{'text-red-600': row.isOverdue, 'text-yellow-600' : !row.isOverdue && row.status === 'Invoiced'}">
									{{ currencyFormatter.format(amountDueWithoutProcessingFee(row)) }}
								</span>
								<div class="md:hidden mt-4">
									<span v-if="!isDueOnReceipt(row.paymentTerms)">
										{{ row.dueDate }}
									</span>
									<span v-else>
										{{ row.paymentTerms }}
									</span>
								</div>
							</template>
						</tw-table-column>
						<tw-table-column
							class-name="hidden md:table-cell"
							header-class-name="hidden md:table-cell"
							header="Invoice Date" 
							prop="invoiceDate" />
						<tw-table-column
							header="Due Date" 
							header-class-name="hidden md:table-cell"
							class-name="hidden md:table-cell"
							prop="dueDate">
							<template #default="{row}">
								<span v-if="!isDueOnReceipt(row.paymentTerms)">
									{{ row.dueDate }}
								</span>
								<span v-else>
									{{ row.paymentTerms }}
								</span>
							</template>
						</tw-table-column>
						<tw-table-column
							class-name="hidden md:block"
							header-class-name="hidden md:block"
							header="Status" 
							prop="status">
							<template #default="{row}">
								<tw-badge-flat
									v-if="showStatusBadge(row)"
									:color="invoiceStatusColor(row)">
									{{ invoiceStatusLabel(row) }}
								</tw-badge-flat>
								<span v-else>
									{{ row.paymentTerms }}
								</span>
							</template>
						</tw-table-column>
						<tw-table-column
							condensed
							class-name="hidden md:table-cell w-44"
							header-class-name="hidden md:table-cell sr-only w-44"
							header="Pay">
							<template #default="{ row }">
								<pay-button
									v-if="isPayable(row) && !loading"
									:invoice="row"
									@submitted="getDashboard"
									@verification-canceled="clearPendingVerification(row)" />
							</template>
						</tw-table-column>
						<tw-table-column
							header="View Details" 
							header-class-name="sr-only"
							class-name="text-center md:text-right">
							<template #default="{row}">
								<InvoiceDetailsButton :invoice="row" />
								<pay-button
									class="md:hidden mt-4"
									v-if="isPayable(row) && !loading"
									:invoice="row"
									@submitted="getDashboard"
									@verification-canceled="clearPendingVerification(row)" />
							</template>
						</tw-table-column>
					</tw-table>
				</div>
			</div>
		</div>

		<BlogPostPopup />
	</div>
</template>

<script lang="ts">
	export default { name: 'DashboardMain' } // name the component
</script>

<script setup lang="ts">
	// api
	import api from '@/utilities/api'

	import {
		isDueOnReceipt,
		isPayable,
		showStatusBadge,
		invoiceStatusColor,
		invoiceStatusLabel,
		clearPendingVerification,
		applyInvoiceDueStatus,
		amountDueWithoutProcessingFee,
		sumAmountDue
	} from '@/utilities/invoiceStatus'

	import {
		showsLicensing
	} from '@/utilities/typeOfWork'

	import {
		Auth
	} from 'aws-amplify'

	// store
	import {
		store as _userStore
	} from '@/store/user'

	// components
	import {
		ArrowDownTrayIcon as ArrowDownTrayIconMini,
		ListBulletIcon as ListBulletIconMini
	} from "@heroicons/vue/20/solid"

	import InvoiceDetailsButton from "../Invoices/components/InvoiceDetailsButton.vue"
	import PayButton from '../Invoices/components/PayButton.vue'
	import BlogPostPopup from './components/BlogPostPopup.vue'

	// vue
	import {
		ref,
		computed,
		onMounted
	} from "vue"

	const userStore = _userStore()

	const loading = ref(true)
	const invoices = ref<Array<any>>([])
	const licenses = ref<Array<any>>([])

	// the same rule as the sidebar menu, so the card and the menu item come and go together. the
	// dashboard payload already carries the licenses, so no separate lookup is needed here
	const showLicensing = computed(() => {
		return showsLicensing(userStore?.user?.cognitoUser?.attributes?.["custom:typeOfWork"], licenses.value.length > 0)
	})

	const currencyFormatter = new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "USD",
	})

	// derived from the invoice list rather than accumulated while fetching, so refetching after a
	// payment recalculates the totals instead of adding to the previous run's values
	const totalOutstanding = computed(() => sumAmountDue(invoices.value))

	const totalPastDue = computed(() => {
		return sumAmountDue(invoices.value.filter((invoice: any) => invoice.isOverdue))
	})

	onMounted(() => {
		getDashboard()
	})

	// pulls the dashboard payload for the logged in contact out of FileMaker
	async function fetchDashboardData() {
		const user = await Auth.currentAuthenticatedUser()

		const body = {
			action: "getDashboard",
			payload: {
				id: user.attributes['custom:fm_contact_id'],
			}
		}
		const response = await api("post", "fmDataAPI", "/fmDataAPI", body)
		const scriptResult = JSON.parse(response.response.scriptResult)

		return scriptResult.result.data
	}

	async function getDashboard() {
		try {
			loading.value = true

			const todayDate = new Date()
			const dashboardData = await fetchDashboardData()

			licenses.value = dashboardData.licenses ?? []
			let _invoices = dashboardData.invoices


			_invoices = _invoices.filter((invoice: any) => {
				return invoice?.status === "Invoiced"
			})

			_invoices.forEach((invoice: any) => {
				//  split invoices summaries string
				invoice.summaries = invoice.summaries.split("\r")

				applyInvoiceDueStatus(invoice, todayDate)
			})

			invoices.value = _invoices
		}
		catch(e) {
			console.error(e)
		}
		finally {
			loading.value = false
		}
	}

</script>
